import React, { useState, useEffect, useCallback } from 'react';
import { router } from '@inertiajs/react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CreditsInfo from '../../Components/CreditsInfo';

const WorkspaceSceneExecution = ({ scenes = [], movieHash = '', executions = [], credits, user_email, free_scene_executions }) => {
  const [selectedSceneNumber, setSelectedSceneNumber] = useState('');
  const [sceneText, setSceneText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [executionsLoading, setExecutionsLoading] = useState(false);
  const [creativeDirection, setCreativeDirection] = useState('');

  useEffect(() => {
    const selected = scenes.find(
      (s) => s.scene_number === parseInt(selectedSceneNumber)
    );
    setSceneText(selected ? selected.text || '' : '');
  }, [selectedSceneNumber, scenes]);

  const handleSceneChange = (e) => {
    setSelectedSceneNumber(e.target.value);
  };

  const handleTextChange = (e) => setSceneText(e.target.value);

  const handleCreativeDirectionChange = (value) => {
    setCreativeDirection(value);
  };

  const handleSubmit = useCallback(() => {
    if (!selectedSceneNumber) {
      toast.warn('Please select a scene to work on.');
      return;
    }

    if (isSubmitting) return; // guard against double clicks

    setIsSubmitting(true);

    // Use traditional form submission to force full page reload
    // This ensures the workspace loads with proper server-side layout
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `/screenplay/${movieHash}/workspace_scene_execution`;
    
    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    if (csrfToken) {
      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = '_token';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }
    
    // Add form data
    const sceneNumberInput = document.createElement('input');
    sceneNumberInput.type = 'hidden';
    sceneNumberInput.name = 'scene_number';
    sceneNumberInput.value = selectedSceneNumber;
    form.appendChild(sceneNumberInput);
    
    const sceneTextInput = document.createElement('input');
    sceneTextInput.type = 'hidden';
    sceneTextInput.name = 'scene_text';
    sceneTextInput.value = sceneText;
    form.appendChild(sceneTextInput);
    
    // Add creative direction data
    const creativeDirectionInput = document.createElement('input');
    creativeDirectionInput.type = 'hidden';
    creativeDirectionInput.name = 'creative_direction';
    creativeDirectionInput.value = creativeDirection;
    form.appendChild(creativeDirectionInput);
    
    // Submit form (this will cause full page navigation)
    document.body.appendChild(form);
    form.submit();

    toast.info('AI processing started — splitting the scene into beats. This typically takes ~30 seconds.');
  }, [selectedSceneNumber, movieHash, sceneText, creativeDirection, isSubmitting]);

  return (
    
    <div className="container py-4">
                  <CreditsInfo
                movie_title='a screenplay'
                movie_hash={movieHash}
                initialCredits={credits}
                user_email={user_email}
            />
<div className="alert alert-info mb-4">
        <strong>Scene Beat Tweak</strong>
        <div className="small">Pick a scene, optionally edit the text, then click "Work on Scene" — AI will split the scene into beats.</div>
      </div>

      <div className="card mt-4">
        <div className="card-body">
          <h5 className="card-title">Existing <strong>Beat Tweaks</strong> for this Screenplay</h5>
          {executionsLoading ? (
            <div>Loading...</div>
          ) : executions && executions.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Scene #</th>
                    <th>Scene Title</th>
                    <th style={{ width: '10%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {executions.map((ex) => {
                    const scene = scenes.find(s => s.scene_number === ex.scene_number);
                    const sceneTitle = scene ? scene.scene_title : 'Untitled Scene';
                    const uuid = ex.uuid || ex.workspace_uuid;

                    return (
                      <tr key={uuid || ex.id}>
                        <td>{ex.date ? new Date(ex.date).toLocaleString() : '-'}</td>
                        <td>{ex.scene_number}</td>
                        <td>{sceneTitle}</td>
                        <td>
                          <a
                            href={`/workspace/${uuid}`}
                            className="btn btn-primary btn-sm float-end"
                          >
                            Continue
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="small text-muted">You haven't used <strong>Beat Tweak</strong> for this screenplay yet.</div>
          )}
        </div>
      </div>

      <div className="mb-3">
        <label htmlFor="sceneSelect" className="form-label">Select Scene</label>
        <select
          id="sceneSelect"
          className="form-select"
          value={selectedSceneNumber}
          onChange={handleSceneChange}
        >
          <option value="">-- Select a Scene --</option>
          {scenes.map((scene) => (
            <option key={scene.scene_number} value={scene.scene_number}>
              {scene.scene_number} - {scene.scene_title}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-3">
        <label htmlFor="sceneText" className="form-label">Scene Text</label>
        <textarea
          id="sceneText"
          className="form-control"
          rows={10}
          value={sceneText}
          onChange={handleTextChange}
          placeholder="Edit scene text here..."
        />
      </div>

      <div className="card mb-3">
        <div className="card-header">
          <h6 className="mb-0">Creative Direction (Optional)</h6>
        </div>
        <div className="card-body">
          <div className="alert alert-info">
            <strong>This is optional</strong>, but providing creative direction can help the AI give more tailored and relevant feedback.
          </div>

          <div className="mb-0">
            <label htmlFor="creativeDirection" className="form-label fw-bold">Creative Direction</label>
            <textarea
              id="creativeDirection"
              className="form-control"
              rows={4}
              value={creativeDirection}
              onChange={(e) => handleCreativeDirectionChange(e.target.value)}
              placeholder="e.g., Focus on building tension through dialogue. The protagonist should seem vulnerable but determined. End with a power shift where the antagonist gains the upper hand."
            />
            <small className="form-text text-muted">Provide high-level guidance for AI feedback on tone, character dynamics, pacing, or story goals for this scene.</small>
          </div>
        </div>
      </div>

      <div className="mb-3">
        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={isSubmitting || !selectedSceneNumber}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Processing...
            </>
          ) : free_scene_executions > 0 ? (
            `Work on Scene (${free_scene_executions} free remaining)`
          ) : (
            'Work on Scene (6 credits)'
          )}
        </button>
      </div>

      {isSubmitting && (
        <div className="alert alert-warning mt-2">
          AI processing in progress. This may take about 30 seconds — please wait.
        </div>
      )}


      <ToastContainer />
    </div>
  );
};

export default WorkspaceSceneExecution;
